import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { Header } from "../components/Header";
import { PublicPricingHeader } from "../components/PublicPricingHeader";
import { PricingTestimonialStrip } from "../components/PricingTestimonialStrip";
import { ResultsDisclaimer } from "../components/ResultsDisclaimer";
import { TrackCountdownBanner } from "../components/TrackCountdownBanner";
import { SocialMeta } from "../components/SocialMeta";
import { Check, ArrowRight, Loader2, Sparkles, Flame } from "lucide-react";
import { toast } from "sonner";
import { VAULT } from "../data/vaultStats";
import { trackEvent } from "../lib/analytics";
import { SiteFooter } from "../components/SiteFooter";
import { CheckoutConsentDialog } from "../components/CheckoutConsentDialog";
import { MobileStickyCta } from "../components/MobileStickyCta";
import { ModelBadge } from "../components/TrackDownloads";

const PLAN_LABELS = {
  starter: { label: "Starter Pack", price: "£7" },
  lifetime: { label: "Stag Vault Complete — Lifetime Access", price: "£25" },
};

const PERKS_FREE = [
  `Browse all ${VAULT.tracks} tracks (read every prompt — even the locked ones)`,
  "Copy & run prompts on Tracks 1–2 (the first 2 income streams)",
  "10 GPT-5.2 AI runs per day",
  "Save up to 5 favourite prompts",
  "Onboarding quiz + progress checkboxes",
];

const PERKS_PAID = [
  "Unlimited AI runs on ChatGPT (GPT-5.2), Claude or Gemini — no daily cap",
  "Download any track as a ZIP you own (prompts, guide, checklist, 30-day plan)",
  `All ${VAULT.tracks} tracks fully unlocked (including every advanced GTM block)`,
  "Save unlimited favourites",
  "12+ done-for-you templates (Notion, Canva, swipe files)",
  "Early access to every new track + future feature",
];

export default function PricingPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(null); // "monthly" | "lifetime" | null
  const [stats, setStats] = useState(null);
  const [consented, setConsented] = useState(false);
  const [pendingPlan, setPendingPlan] = useState(null);
  const [bundles, setBundles] = useState([]);

  useEffect(() => { trackEvent("PricingViewed", { page: "pricing" }); }, []);

  useEffect(() => {
    let mounted = true;
    api.get("/billing/lifetime-stats")
      .then(({ data }) => { if (mounted) setStats(data); })
      .catch(() => {});
    api.get("/bundles")
      .then(({ data }) => { if (mounted) setBundles(data.bundles || []); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  if (user === null) return null;
  const isAuthed = user && typeof user === "object";
  const isLifetime = isAuthed && user.tier === "lifetime";
  const isMonthly = isAuthed && user.tier === "monthly";
  const isFree = isAuthed && user.tier === "free";
  const soldOut = stats?.sold_out;
  const pct = stats ? Math.min(100, Math.round((stats.taken / stats.cap) * 100)) : 0;

  const startCheckout = async (plan) => {
    if (!isAuthed) {
      // Cold traffic → send them through signup with plan intent so we can return them here.
      window.location.href = `/initiate?next=/pricing&plan=${encodeURIComponent(plan)}`;
      return;
    }
    if (!consented) {
      setPendingPlan(plan);
      return;
    }
    await launchCheckout(plan);
  };

  const confirmConsent = async () => {
    setConsented(true);
    await launchCheckout(pendingPlan);
  };

  const launchCheckout = async (plan) => {
    setLoading(plan);
    try {
      const { data } = await api.post("/billing/create-checkout-session", {
        origin_url: window.location.origin,
        plan,
        rewardful_referral: (typeof window !== "undefined" && window.Rewardful && window.Rewardful.referral) || null,
        consent_no_refund: true,
        consent_timestamp: new Date().toISOString(),
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (e) {
      setLoading(null);
      setPendingPlan(null);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout");
    }
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="pricing-page">
      <SocialMeta
        title="Pricing — Stag Vault · £25 lifetime, no subscription required"
        description={`One-time £25 unlocks all ${VAULT.tracks} income tracks and ${VAULT.promptsFmt} AI prompts, forever. Every future drop free. 14-day no-questions refund. No subscription required, no upsell tree.`}
        canonical="https://stagvault.co.uk/pricing"
        url="https://stagvault.co.uk/pricing"
      />
      <TrackCountdownBanner />
      {isAuthed ? <Header /> : <PublicPricingHeader />}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="text-center">
          <div className="text-purple-400 text-sm tracking-[0.35em] uppercase mb-6" data-testid="pricing-hero-kicker">
            Stag Vault Complete
          </div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]">
            From AI idea to{" "}
            <span className="text-purple-400 drop-shadow-[0_0_30px_rgba(168,85,247,0.45)]">
              sellable offer.
            </span>
          </h1>
          <p className="mt-8 text-zinc-300 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
            Free gives you the first 2 tracks &amp; 10 AI runs a day. Lifetime Access unlocks all {VAULT.tracks} income tracks ({VAULT.promptsFmt} prompts), every advanced GTM block, and unlimited runs.
          </p>

          {/* Founder trust note — pre-empts the "is this real?" objection */}
          <div
            className="mt-10 max-w-2xl mx-auto border-l-2 border-purple-500/50 pl-5 py-3 text-left bg-purple-500/[0.04]"
            data-testid="pricing-founder-note"
          >
            <div className="text-purple-300 text-[10px] tracking-[0.35em] uppercase font-mono mb-2">
              Why £25?
            </div>
            <p className="text-zinc-300 text-sm md:text-[15px] leading-relaxed">
              Because <span className="text-white font-semibold">£25 one-time</span> shouldn't gatekeep anyone — and this is my TikTok launch, not a course-guru scam. One payment, lifetime access, every future drop included. Get in while I'm still one person answering my own DMs.
            </p>
            <p className="mt-3 text-purple-300/80 text-xs tracking-[0.15em]">
              — Ross · Founder
            </p>
          </div>
        </div>

        {/* Free-preview reassurance strip */}
        <div className="mt-8 flex flex-col md:flex-row items-center justify-center gap-3 text-center md:text-left" data-testid="free-preview-strip">
          <span className="text-[10px] tracking-[0.35em] uppercase text-purple-300 font-mono">
            Wavering?
          </span>
          <Link
            to="/library-preview"
            className="inline-flex items-center gap-2 border border-purple-500/40 hover:bg-purple-500/10 text-purple-200 hover:text-white px-4 py-2 text-xs tracking-[0.2em] uppercase transition-colors"
            data-testid="pricing-to-preview"
          >
            Browse 20 real prompts free →
          </Link>
          <Link
            to="/quiz"
            className="inline-flex items-center gap-2 border border-amber-400/40 hover:bg-amber-500/10 text-amber-200 hover:text-white px-4 py-2 text-xs tracking-[0.2em] uppercase transition-colors"
            data-testid="pricing-to-quiz"
          >
            Or take the 60-sec quiz →
          </Link>
          <span className="text-[10px] text-zinc-500 hidden md:inline">No signup. Full text. Copy them.</span>
        </div>

        {/* Modular bundles · 3 × £15 alternative to Complete */}
        {bundles.length > 0 && (
          <div className="mt-14" data-testid="bundles-strip">
            <div className="text-center mb-8">
              <div className="text-[10px] tracking-[0.35em] uppercase text-purple-300 font-mono mb-2">
                Only need one lane? · Modular bundles
              </div>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-zinc-50">
                Pick a bundle for £15 · or lock the lot at £25
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {bundles.map((b) => (
                <Link
                  key={b.key}
                  to={`/bundle/${b.key}`}
                  className="group border border-zinc-800 hover:border-purple-500 bg-[#0a070d] p-5 flex flex-col transition-all"
                  data-testid={`bundle-card-${b.key}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-purple-300 font-mono">
                      {b.name.replace(" Bundle", "")}
                    </div>
                    <div className="font-display text-2xl font-black text-white">£{b.price_gbp}</div>
                  </div>
                  <p className="text-sm text-zinc-400 leading-relaxed mb-4 flex-1">
                    {b.tagline}
                  </p>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-zinc-500 font-mono">
                      {b.track_count} tracks · {b.prompt_count.toLocaleString()} prompts
                    </span>
                    <span className="text-purple-300 group-hover:text-purple-200 tracking-widest uppercase text-[10px]">
                      Explore →
                    </span>
                  </div>
                </Link>
              ))}
            </div>
            <p className="mt-5 text-center text-[11px] text-zinc-600 tracking-wider">
              £15 × 3 bundles = £45 · Stag Vault Complete = £25 · Complete pays for itself immediately.
            </p>
          </div>
        )}

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto" data-testid="toolkit-ladder-preview">
          {/* Starter Pack — £7 tripwire (LIVE) */}
          <div className="bg-[#0a070d] border border-amber-400/50 p-6 md:p-7 relative shadow-[0_0_18px_-6px_rgba(176,112,255,0.35)]" data-testid="ladder-starter">
            <div className="absolute -top-2.5 left-4 inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500 text-black text-[9px] tracking-[0.3em] uppercase font-bold">
              Live · Try-first
            </div>
            <div className="text-amber-300 text-[10px] tracking-[0.3em] uppercase mb-3">Starter Pack</div>
            <div className="flex items-baseline gap-2 mb-3">
              <span className="font-display text-4xl font-black text-white">£7</span>
              <span className="text-zinc-500 text-sm">one-time · try-first</span>
            </div>
            <p className="text-zinc-300 text-sm leading-relaxed mb-4">
              5 fastest-to-build AI products from Track 38. Ship your first one this weekend.
            </p>
            <div className="text-zinc-500 text-xs tracking-wide mb-5">
              Best for: first-time AI buyers testing the water.
            </div>
            {(isLifetime || isMonthly) ? (
              <div className="w-full border border-zinc-800 bg-zinc-900/40 text-zinc-500 px-4 py-2.5 text-xs tracking-[0.2em] uppercase text-center" data-testid="starter-already-included">
                Included in your plan
              </div>
            ) : isAuthed && user.tier === "starter" ? (
              <Link
                to="/starter-pack"
                className="block w-full text-center bg-amber-500 hover:bg-amber-400 text-black px-4 py-2.5 text-xs tracking-[0.25em] uppercase font-bold transition-all"
                data-testid="starter-open-pack"
              >
                Open my Starter Pack
              </Link>
            ) : (
              <button
                onClick={() => startCheckout("starter")}
                disabled={loading !== null}
                className="w-full border border-amber-400 text-amber-300 hover:bg-amber-500 hover:text-black px-4 py-2.5 text-xs tracking-[0.25em] uppercase font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                data-testid="checkout-starter-button"
              >
                {loading === "starter" ? (<><Loader2 size={14} className="animate-spin" /> Redirecting...</>) : isAuthed ? (<>Get for £7 <ArrowRight size={14} /></>) : (<>Try for £7 <ArrowRight size={14} /></>)}
              </button>
            )}
          </div>

          {/* Stag Vault Complete — the flagship */}
          <div className="bg-[#0a070d] border border-purple-500 p-6 md:p-7 relative shadow-[0_0_30px_rgba(168,85,247,0.3)]" data-testid="ladder-lifetime-preview">
            <div className="absolute -top-2.5 left-4 inline-flex items-center gap-1 px-2 py-0.5 bg-purple-500 text-black text-[9px] tracking-[0.3em] uppercase font-bold">
              Best Value
            </div>
            <div className="text-purple-300 text-[10px] tracking-[0.3em] uppercase mb-3">Stag Vault Complete</div>
            <div className="flex items-baseline gap-2 mb-3">
              <span className="font-display text-4xl font-black text-white">£25</span>
              <span className="text-zinc-500 text-sm">Lifetime Access · one payment</span>
            </div>
            <p className="text-zinc-300 text-sm leading-relaxed mb-4">
              All {VAULT.tracks} income tracks. All {VAULT.promptsFmt} prompts. Every future drop. Live now — see full details below.
            </p>
            <div className="text-purple-300 text-xs tracking-wide font-semibold">
              Best for: serious builders who want the full toolkit.
            </div>
          </div>

          {/* Founder's Circle removed per user request (£97 tier retired). */}
        </div>

        {/* Money-back guarantee band */}
        <div className="mt-8 flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8 py-5 border-t border-b border-amber-500/15 text-center" data-testid="pricing-guarantee-band">
          <div className="inline-flex items-center gap-2 text-amber-300 text-sm font-bold">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
            14-day no-questions refund
          </div>
          <div className="text-zinc-500 text-sm hidden md:block">·</div>
          <div className="inline-flex items-center gap-2 text-zinc-400 text-sm">
            Not what you expected? Email us within 14 days, get a full refund. No hassle, no forms.
          </div>
        </div>

        <div className="mt-16 pt-2">
          <div className="text-zinc-500 text-xs tracking-[0.3em] uppercase text-center mb-6">Current plans · live now</div>
        </div>

        <div className={`mt-4 grid grid-cols-1 gap-6 ${isMonthly ? "md:grid-cols-3" : "md:grid-cols-2 max-w-4xl mx-auto"}`}>
          {/* FREE */}
          <div className="bg-[#0a0a0a] border border-zinc-800 p-8" data-testid="tier-free">
            <div className="text-zinc-500 text-sm tracking-[0.25em] uppercase mb-3">Free</div>
            <div className="flex items-baseline gap-2 mb-2">
              <span className="font-display text-5xl font-black tracking-tighter text-white">£0</span>
              <span className="text-zinc-500 text-base">forever</span>
            </div>
            <p className="text-zinc-400 text-base mb-8">Browse, copy, and ship from the first 2 tracks.</p>
            <ul className="space-y-3 mb-8 min-h-[210px]">
              {PERKS_FREE.map((p) => (
                <li key={p} className="flex items-start gap-3 text-zinc-300 text-sm leading-relaxed">
                  <Check size={16} className="text-zinc-500 shrink-0 mt-1" />
                  {p}
                </li>
              ))}
            </ul>
            <div className="text-zinc-600 text-sm tracking-wide" data-testid="free-plan-label">
              {isFree ? "Your current plan" : "Free tier"}
            </div>
          </div>

          {/* MONTHLY plan retired — existing subscribers see their status here, no new sign-ups */}
          {isMonthly && (
            <div className="bg-[#0a0a0a] border border-purple-500/60 p-8 relative" data-testid="tier-monthly">
              <div className="absolute -top-px left-0 w-24 h-px bg-gradient-to-r from-purple-500 to-transparent" />
              <div className="text-purple-400 text-sm tracking-[0.25em] uppercase mb-3" data-testid="tier-monthly-name">Stag Vault Complete — Monthly</div>
              <p className="text-zinc-300 text-base mb-8">Your existing monthly membership. Unlimited everything while active — manage or cancel any time from your account.</p>
              <ul className="space-y-3 mb-8 min-h-[210px]">
                {PERKS_PAID.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-zinc-200 text-sm leading-relaxed">
                    <Check size={16} className="text-purple-400 shrink-0 mt-1" />
                    {p}
                  </li>
                ))}
              </ul>
              <div className="border border-purple-500/40 bg-purple-500/10 text-purple-300 px-6 py-4 text-sm tracking-wide text-center" data-testid="already-monthly">
                Active subscription
              </div>
              <ResultsDisclaimer variant="short" className="mt-3 text-center" testId="pricing-monthly-disclaimer" />
            </div>
          )}

          {/* LIFETIME — recommended */}
          <div className="bg-[#0a0a0a] border border-purple-500 p-8 relative shadow-[0_0_40px_rgba(168,85,247,0.22)]" data-testid="tier-lifetime">
            <div className="absolute -top-px left-0 w-24 h-px bg-gradient-to-r from-purple-500 to-transparent" />
            <div className="absolute -top-px right-0 w-24 h-px bg-gradient-to-l from-purple-500 to-transparent" />
            <div className="absolute -top-3 right-4 bg-purple-500 text-black text-[10px] font-bold tracking-[0.25em] uppercase px-3 py-1 inline-flex items-center gap-1">
              <Sparkles size={11} /> Best value
            </div>
            <div className="text-purple-400 text-sm tracking-[0.25em] uppercase mb-1" data-testid="tier-lifetime-name">Stag Vault Complete</div>
            <div className="text-zinc-400 text-xs tracking-[0.2em] uppercase mb-3" data-testid="tier-lifetime-subline">Lifetime Access — £{stats?.price ?? 25}</div>
            <div className="flex items-baseline gap-2 mb-2">
              <span className="font-display text-5xl font-black tracking-tighter text-white">£{stats?.price ?? 25}</span>
              <span className="text-zinc-500 text-base">one-time</span>
            </div>
            <p className="text-zinc-300 text-base mb-2">Pay once. Unlock forever. Cheaper than one AI course.</p>
            <div className="mb-4 flex flex-wrap items-center gap-3"><ModelBadge testId="pricing-model-badge" /><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-300">+ download the whole vault</span></div>

            {stats && !isLifetime && (
              <div
                className={`mb-6 px-3 py-2 border ${soldOut ? "border-red-500/50 bg-red-500/10" : "border-purple-500/40 bg-purple-500/[0.06]"}`}
                data-testid="lifetime-scarcity"
              >
                <div className="flex items-center justify-between text-xs tracking-[0.2em] uppercase mb-2">
                  <span className={`inline-flex items-center gap-1.5 ${soldOut ? "text-red-300" : "text-purple-300"}`}>
                    <Flame size={11} />
                    {soldOut ? "Sold out" : "Founding 100"}
                  </span>
                  <span className="text-zinc-400" data-testid="lifetime-counter">
                    {stats.taken} / {stats.cap}
                  </span>
                </div>
                <div className="h-1 bg-zinc-800 overflow-hidden">
                  <div
                    className={`h-full transition-all duration-500 ${soldOut ? "bg-red-500" : "bg-purple-500"}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                {!soldOut && (
                  <div className="text-[10px] tracking-wide text-zinc-500 mt-2" data-testid="lifetime-remaining">
                    Only {stats.remaining} lifetime {stats.remaining === 1 ? "seat" : "seats"} left at £{stats.price}. Locked in forever.
                  </div>
                )}
              </div>
            )}

            <ul className="space-y-3 mb-8 min-h-[210px]">
              {PERKS_PAID.map((p) => (
                <li key={p} className="flex items-start gap-3 text-zinc-200 text-sm leading-relaxed">
                  <Check size={16} className="text-purple-400 shrink-0 mt-1" />
                  {p}
                </li>
              ))}
              <li className="flex items-start gap-3 text-zinc-200 text-sm leading-relaxed font-medium">
                <Check size={16} className="text-purple-400 shrink-0 mt-1" />
                Never billed again. Ever.
              </li>
            </ul>
            {isLifetime ? (
              <div className="border border-purple-500/40 bg-purple-500/10 text-purple-300 px-6 py-4 text-sm tracking-wide text-center" data-testid="already-lifetime">
                You have full access.
              </div>
            ) : soldOut ? (
              <div className="border border-zinc-800 bg-zinc-900/40 text-zinc-400 px-6 py-4 text-sm tracking-wide text-center" data-testid="lifetime-sold-out">
                Sold out — all founding lifetime seats have been claimed
              </div>
            ) : (
              <button
                onClick={() => startCheckout("lifetime")}
                disabled={loading !== null}
                className="w-full bg-purple-500 hover:bg-purple-400 text-black px-8 py-4 text-sm tracking-[0.25em] uppercase font-bold transition-all duration-300 shadow-[0_0_25px_rgba(168,85,247,0.4)] hover:shadow-[0_0_45px_rgba(168,85,247,0.75)] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-3"
                data-testid="checkout-lifetime-button"
              >
                {loading === "lifetime" ? (<><Loader2 size={18} className="animate-spin" /> Redirecting...</>) : isAuthed ? (<>Get lifetime <ArrowRight size={18} /></>) : (<>Claim lifetime seat <ArrowRight size={18} /></>)}
              </button>
            )}
            <p className="text-zinc-600 text-xs tracking-wide mt-4 text-center">
              One payment · Immediate access · Powered by Stripe
            </p>
            <p className="text-amber-400/70 text-[10px] tracking-wide mt-2 text-center leading-relaxed" data-testid="amex-warning-lifetime">
              Using a discount code? Please pay with a Visa or Mastercard — Amex often rejects zero-value verifications on 100%-off orders. Want only some tracks? <Link to="/build-your-own-vault" className="text-purple-300 underline underline-offset-4" data-testid="pricing-byov-link">Build your own vault</Link> — 10 for £10 or 15 for £15. Been given a Stag Vault <Link to="/redeem" className="text-purple-300 underline underline-offset-4" data-testid="pricing-access-code-link">Lifetime Access code</Link>? Enter it there instead of at checkout.
            </p>
          </div>
        </div>

        {/* Social proof strip — sits directly under tier cards at the moment of decision */}
        <PricingTestimonialStrip />
        <ResultsDisclaimer className="mx-auto mt-10 max-w-3xl" testId="pricing-results-disclaimer" />

        {!isLifetime && !soldOut && (
          <MobileStickyCta label="Stag Vault Complete · Lifetime" note={`${VAULT.tracks} tracks · one payment`} price="Unlock £25" loading={loading === "lifetime"} disabled={loading !== null} onClick={() => startCheckout("lifetime")} testId="pricing-sticky-cta" />
        )}
        <CheckoutConsentDialog
          open={pendingPlan !== null}
          onOpenChange={(o) => { if (!o && loading === null) setPendingPlan(null); }}
          onConfirm={confirmConsent}
          label={PLAN_LABELS[pendingPlan]?.label || "Checkout"}
          price={PLAN_LABELS[pendingPlan]?.price}
          loading={loading !== null}
        />

        <div className="mt-12 text-center">
          <Link
            to={isAuthed ? "/vault" : "/"}
            className="text-zinc-500 hover:text-purple-300 text-sm tracking-wide transition-colors"
            data-testid="back-to-vault-link"
          >
            ← {isAuthed ? "Back to the vault" : "Back to home"}
          </Link>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
